<template>
  <v-container fluid class="px-12 py-2" v-if="initialLoadComplete">
    <v-row justify="center" v-if="showForm">
      <v-col cols="12">
        <breadcrumbs></breadcrumbs>
        <h1>{{ id ? "Edit" : "Create" }} Primary Role</h1>

        <v-form @submit.prevent="onSubmit" v-model="valid" class="mt-8">
          <label class="och-label"> Role Name </label>
          <v-text-field :rules="rules" v-model="name" outlined></v-text-field>

          <label class="och-label"> Description </label>
          <v-textarea
            :rules="rules"
            counter="255"
            outlined
            v-model="description"
          ></v-textarea>
        </v-form>

        <div class="text-right mt-8">
          <v-btn v-if="id" @click="showForm = false" outlined class="mr-2"
            >Cancel</v-btn
          >
          <v-btn
            :disabled="!valid"
            @click="savePrimaryRole"
            color="primary"
            class="elevation-0"
            >Save</v-btn
          >
        </div>
      </v-col>
    </v-row>
    <v-row justify="center" v-else>
      <v-col cols="12">
        <v-row>
          <v-col cols="9">
            <breadcrumbs></breadcrumbs>
            <h1>{{ this.record.name }}</h1></v-col
          >
          <v-col cols="3" class="text-right">
            <v-btn outlined color="primary" @click="edit">Edit</v-btn>
          </v-col>
        </v-row>

        <div class="mt-2">
          <div class="caption">Description</div>
          {{ this.record.description }}
        </div>
        <v-divider class="my-5"></v-divider>
      </v-col>
    </v-row>

    <div v-if="id && !showForm">
      <div class="title">Base Security Functions</div>
      <v-autocomplete
        :items="unassociatedFunctionList"
        :search-input.sync="search"
        class="mt-3 mb-5"
        clearable
        hide-details
        item-text="name"
        no-data-text="No more functions found to associate"
        outlined
        placeholder="Search for a base security function to assocate with primary role..."
        return-object
        v-model="selectedFunction"
      >
        <template v-slot:append-outer>
          <div class="mt-n1 ml-4">
            <v-btn
              color="primary"
              outlined
              @click="associateFunction(selectedFunction)"
              :disabled="!selectedFunction"
              >Add</v-btn
            >
          </div>
        </template>
      </v-autocomplete>
      <function-table
        :hideSearch="true"
        :list="associatedFunctionList"
        @remove="disassociateFunction"
      ></function-table>

      <div class="title mt-12">Questions</div>
      <v-row>
        <v-col cols="12"
          ><v-autocomplete
            :items="unassociatedQuestionList"
            :search-input.sync="searchQuestion"
            class="mt-3 mb-5"
            clearable
            hide-details
            item-text="text"
            no-data-text="No more questions found to associate"
            outlined
            placeholder="Search for a question to assocate with primary role..."
            return-object
            v-model="selectedQuestion"
          >
            <template v-slot:append-outer>
              <div class="mt-n1 ml-4">
                <v-btn
                  color="primary"
                  outlined
                  @click="associateQuestion(selectedQuestion)"
                  :disabled="!selectedQuestion"
                  >Add</v-btn
                >
              </div>
            </template>
          </v-autocomplete></v-col
        >
      </v-row>
      <question-table></question-table>
    </div>
  </v-container>
</template>
<script>
import { cloneDeep } from "lodash";
import { mapActions, mapGetters, mapState } from "vuex";
import Breadcrumbs from "../Breadcrumbs";
import FunctionTable from "./RoleFunctionTable";
import QuestionTable from "./RoleQuestionTable";

export default {
  components: {
    Breadcrumbs,
    FunctionTable,
    QuestionTable,
  },
  computed: {
    associatedFunctionList() {
      const primaryRoleFunctionList = this.filterPrimaryRoleFunctionList(
        "secRolePrimaryId",
        this.id
      );

      // Takes each associated function and adds in the name of it
      const list = primaryRoleFunctionList.map(item => {
        if (!this.functionList) return;

        const match = this.functionList.find(func => {
          return func.id === item.secFunctionId;
        });
        return Object.assign({}, item, { name: match.name });
      });

      return list.sort((a, b) => (a.name > b.name ? 1 : 0));
    },
    id() {
      return parseInt(this.$route.params.primaryRoleId, 10);
    },
    unassociatedFunctionList() {
      const filteredIdList = this.associatedFunctionList.map(
        item => item.secFunctionId
      );
      return this.functionList
        .filter(item => !filteredIdList.includes(item.id))
        .sort((a, b) => (a.name > b.name ? 1 : 0));
    },
    unassociatedQuestionList() {
      const filteredIdList = this.filterPrimaryRoleQuestionList(
        "secRolePrimaryId",
        this.id
      ).map(item => item.secQuestionId);

      return this.questionList
        .filter(
          item => !(filteredIdList.includes(item.id) || item.isDeprecated)
        )
        .sort((a, b) => (a.name > b.name ? 1 : 0));
    },
    ...mapGetters({
      filterListByAttribute: "secFunction/filterByAttributeValue",
      filterPrimaryRoleFunctionList:
        "secRolePrimarySecFunction/filterByAttributeValue",
      filterPrimaryRoleQuestionList:
        "secRolePrimarySecQuestion/filterByAttributeValue",
    }),
    ...mapState({
      record: state => state.secRolePrimary.record,
      functionList: state => state.secFunction.list,
      primaryRoleFunctionList: state => state.secRolePrimarySecFunction.list,
      primaryRoleQuestionList: state => state.secRolePrimarySecQuestion.list,
      questionList: state => state.secQuestion.list,
    }),
  },
  async created() {
    try {
      await Promise.all([
        this.getFunctionList(),
        this.getFunctionInputList(),
        this.getPrimaryRoleFunctionList(),
        this.getPrimaryRoleQuestionList(),
        this.getQuestionList(),
      ]);
    } catch (e) {
      console.error(e);
      this.loadError();
    }

    if (this.id) {
      try {
        await this.getRole(this.id);
      } catch (e) {
        console.error(e);
        this.loadError({ message: "Could not retrieve primary role" });
      }
    } else {
      this.showForm = true;
    }

    this.initialLoadComplete = true;
  },
  data() {
    return {
      description: null,
      headers: [
        {
          text: "Name",
          value: "name",
          align: "start",
        },
        {
          text: "Description",
          value: "description",
          align: "start",
        },
      ],
      initialLoadComplete: false,
      name: null,
      rules: [v => !!v || "Required field"],
      search: null,
      searchQuestion: null,
      selectedFunction: null,
      selectedQuestion: null,
      showForm: false,
      valid: false,
    };
  },
  methods: {
    async associateFunction(secFunction) {
      if (!secFunction) return;

      try {
        await this.addFunction({
          secFunctionId: secFunction.id,
          secRolePrimaryId: this.id,
        });
        this.selectedFunction = null;
      } catch (e) {
        console.error(e);
        this.notifyAssociatedFunctionSaveError();
      }
    },
    async associateQuestion(secQuestion) {
      if (!secQuestion) return;
      const associatedQuestionList = this.filterPrimaryRoleQuestionList(
        "secRolePrimaryId",
        this.id,
        [{ entity: "secQuestion", attribute: "secQuestionId" }]
      );

      const orderNumber = associatedQuestionList.length + 1;

      try {
        await this.addQuestion({
          secQuestionId: secQuestion.id,
          secRolePrimaryId: this.id,
          orderNumber,
        });
        this.selectedQuestion = null;
      } catch (e) {
        console.error(e);
        this.notifyAssociatedQuestionSaveError();
      }
    },
    async disassociateFunction({ id: primaryRoleFunctionId = null }) {
      if (!primaryRoleFunctionId) return;
      await this.removeFunction(primaryRoleFunctionId);
    },
    edit() {
      const { name, description } = cloneDeep(this.record);
      this.name = name;
      this.description = description;
      this.showForm = true;
    },
    async savePrimaryRole() {
      if (this.id) {
        await this.update([
          this.id,
          {
            description: this.description,
            name: this.name,
          },
        ]);
      } else {
        const result = await this.create({
          description: this.description,
          name: this.name,
        });
        this.$router.push({
          name: "ochinUserSecurityPrimaryRoleForm",
          params: { primaryRoleId: result.id },
        });
      }
      this.showForm = false;
    },
    ...mapActions({
      addFunction: "secRolePrimarySecFunction/create",
      addQuestion: "secRolePrimarySecQuestion/create",
      create: "secRolePrimary/create",
      getFunctionInputList: "secFunctionInput/list",
      getFunctionList: "secFunction/list",
      getList: "secRolePrimary/list",
      getPrimaryRoleFunctionList: "secRolePrimarySecFunction/list",
      getPrimaryRoleQuestionList: "secRolePrimarySecQuestion/list",
      getQuestionList: "secQuestion/list",
      getRole: "secRolePrimary/get",
      removeFunction: "secRolePrimarySecFunction/remove",
      update: "secRolePrimary/update",
    }),
  },
  notifications: {
    loadError: {
      message: "Could not load initial page state. Refresh page and try again.",
      timeout: 0,
    },
    notifyAssociatedQuestionSaveError: {
      message: "Could not associate question with primary role.",
    },
    notifyAssociatedFunctionSaveError: {
      message: "Could not associate function with primary role.",
    },
  },
};
</script>
